<template>
  <v-form @submit.prevent="$emit('save', patientStory)">
    <v-card>
      <v-card-title>
        {{ $t('edit_patient_story') }}
      </v-card-title>

      <v-card-text>
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="patientStory.first_name"
              :error-messages="errors['first_name']"
              :label="formMixin_getRequiredFieldLabel($t('first_name'))"
              name="first_name"
              autofocus
              @input="formMixin_clearErrors('first_name')"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="patientStory.info"
              type="text"
              :error-messages="errors['info']"
              :label="formMixin_getRequiredFieldLabel($t('info+'))"
              name="info"
              @input="formMixin_clearErrors('info')"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-checkbox
              v-model="patientStory.is_published"
              :error-messages="errors['is_published']"
              :label="$t('published')"
              @change="formMixin_clearErrors('is_published')"
            />
            <v-checkbox
              v-model="patientStory.show_in_homepage"
              :error-messages="errors['show_in_homepage']"
              :label="$t('show_in_homepage')"
              class="mt-0"
              @change="formMixin_clearErrors('show_in_homepage')"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <p class="publish-in-title">
              {{ $t('publish_in') + ': ' }}
            </p>
            <v-checkbox
              class="ma-0"
              v-model="patientStory.show_in_preterm_birth"
              :error-messages="errors['show_in_preterm_birth']"
              :label="$t('preterm_birth')"
              @change="formMixin_clearErrors('show_in_preterm_birth')"
            />
            <v-checkbox
              class="ma-0"
              v-model="patientStory.show_in_premature_baby"
              :error-messages="errors['show_in_premature_baby']"
              :label="$t('premature_baby')"
              @change="formMixin_clearErrors('show_in_premature_baby')"
            />
            <v-checkbox
              class="ma-0"
              v-model="patientStory.show_in_lost_baby"
              :error-messages="errors['show_in_lost_baby']"
              :label="$t('lost_baby')"
              @change="formMixin_clearErrors('show_in_lost_baby')"
            />
            <v-checkbox
              class="ma-0"
              v-model="patientStory.show_in_already_at_home"
              :error-messages="errors['show_in_already_at_home']"
              :label="$t('already_at_home')"
              @change="formMixin_clearErrors('show_in_already_at_home')"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <template v-if="!patientStory.image">
              <BaseFileDropZone
                accept=".jpeg,.jpg,.bmp,.png,.gif"
                @drop="onImageUpload($event, 'image', 'App\\Models\\PatientStory', patientStory.id)"
              >
                <div
                  class="drop-zone"
                  :class="errors.hasOwnProperty('images.0') ? 'error-outline' : ''"
                >
                  <div class="drop-zone-message">
                    <p>{{ $t('file_drop_zone_message') }}</p>
                  </div>
                </div>
              </BaseFileDropZone>
              <!-- we don't clear error, because we don't know image index.
          Instead error will not be visible once we upload the file -->
              <template v-for="(value, key) in errors">
                <div v-if="key.includes('images')" :key="key">
                  <div
                    v-for="(error, errorIndex) in value"
                    :key="errorIndex"
                    class="mt-2 v-messages theme--light error--text"
                  >
                    {{ error }}
                  </div>
                </div>
              </template>
            </template>

            <div v-else class="pl-10">
              <v-img
                v-if="patientStory.image"
                :src="$backendUrl(patientStory.image.image_path)"
                :alt="patientStory.image.original_name"
                width="100px"
                height="100px"
                class="patient-story-img"
              />
              <div class="d-flex justify-space-between name-box">
                <p v-if="patientStory.image" class="image-name text-truncate">
                  {{ patientStory.image.original_name }}
                </p>
                <v-btn icon @click="onImageDelete(patientStory.image)"
                  ><v-icon>mdi-delete</v-icon></v-btn
                >
              </div>
            </div>
          </v-col>

          <v-col cols="12">
            <BaseTextEditor
              v-model="patientStory.description"
              :error-messages="errors['description']"
              :label="formMixin_getRequiredFieldLabel($t('description'))"
              name="description"
              required
              @input="formMixin_clearErrors('description')"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <span class="text-subtitle-2 ml-3"> * {{ $t('must_be_filled') }} </span>

        <v-spacer />

        <v-btn text color="secondary" @click="$emit('preview', patientStory)">{{
          $t('preview')
        }}</v-btn>

        <v-btn text color="primary" @click="$emit('cancel')">
          {{ $t('cancel') }}
        </v-btn>

        <v-btn :disabled="disabled" :loading="disabled" color="primary" text type="submit">
          {{ $t('save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
import formMixin from '../mixins/form-mixin';
import BaseFileDropZone from './base/BaseFileDropZone';
import BaseTextEditor from './base/BaseTextEditor';

export default {
  name: 'PatientStoryForm',

  components: { BaseTextEditor, BaseFileDropZone },

  mixins: [formMixin],

  props: {
    patientStory: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  methods: {
    onImageUpload(images, type, modelType, modelId) {
      this.$emit('upload:image', images, type, modelType, modelId);
    },

    onImageDelete(image) {
      this.$emit('delete:image', image);
    },
  },
};
</script>

<style scoped>
.drop-zone {
  background-color: white;
  outline: 2px dashed darkgray;
  width: 95%;
  height: 100px;
  text-align: center;
  margin: auto;
  position: relative;
}
.drop-zone-message {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
}
.drop-zone-message p,
.image-name {
  color: darkgray;
  margin: 0;
}
.name-box {
  width: 120px;
}
.publish-in-title {
  font-size: 1rem;
  color: gray;
  margin-top: 20px;
}
</style>
